import React, { useEffect, useState } from "react";
import { useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";
import { Box, Container, Paper } from "@mui/material";
import { Header } from "../../../components";
import { PolicyEditor } from "../../../features";

import { useNotify } from "../../../components/@common/NotificationsProvider";
import { useApolloQuery } from "../../../hooks";
import { PolicyInputData } from "../../../@lib/types";
import { useAuthSession } from "../../../components/AuthSessionProvider";

const fetchPolicyQueryDocument = gql`
  query fetchPolicyQueryDocument($policyId: ID!) {
    policy(id: $policyId) {
      id
      title
      description
      documentTitle
      date
      link
      picture {
        source
        originalFilename
      }
      document {
        source
        originalFilename
      }
      country {
        id
      }
      theme {
        id
      }
      geographicalArea {
        id
      }
      policyCategory {
        id
      }
      practices {
        title
        value: id
      }
      people {
        title
        firstname
        lastname
        value: id
      }
    }
  }
`;

const updatePolicyMutationDocument = gql`
  mutation updatePolicyMutation($policyUpdateInput: PolicyUpdateInput!) {
    policyUpdate(input: $policyUpdateInput) {
      policy {
        id
      }
    }
  }
`;

const PolicyEdit: React.FC = () => {
  const [selectedListPractices, setSelectedListPractices] = useState<any>([]);
  const [selectedListPeople, setSelectedListPeople] = useState<any>([]);
  const [documentAttachmentDetails, setDocumentAttachmentDetails] =
    useState<any>(null);
  const [imageAttachmentDetails, setImageAttachmentDetails] =
    useState<any>(null);

  const notify = useNotify();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const isAdmin = location?.pathname?.includes(`admin`);

  const { user } = useAuthSession() as any;

  const { data, loading } = useApolloQuery({
    queryDocument: fetchPolicyQueryDocument,
    variables: { policyId: params.policyId },
  });

  useEffect(() => {
    if (data?.policy?.practices?.length > 0) {
      const modifiedData = data?.policy?.practices?.map((item: any) => {
        return { label: item?.title, value: item?.value };
      });
      setSelectedListPractices(modifiedData);
    }

    if (data?.policy?.people?.length > 0) {
      const modifiedData = data?.policy?.people?.map((item: any) => {
        return {
          label: `${item?.firstname} ${item?.lastname} `,
          value: item?.value,
        };
      });
      setSelectedListPeople(modifiedData);
    }
    if (data?.policy?.picture) {
      setImageAttachmentDetails(data?.policy?.picture);
    }
    if (data?.policy?.document) {
      setDocumentAttachmentDetails(data?.policy?.document);
    }
  }, [data]);

  const [updatePolicy, { loading: updating }] = useMutation(
    updatePolicyMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const initialValues: PolicyInputData | undefined = useMemo(() => {
    if (!data?.policy) return undefined;

    // Destructure first to get rid of values not accepted as of type `PolicyInput`.
    const {
      id,
      country,
      theme,
      geographicalArea,
      policyCategory,
      ...otherFields
    } = data.policy;

    // Re-structure and return `PolicyInput` type object.
    return {
      ...otherFields,
      countryId: country.id,
      geographicalAreaId: geographicalArea.id,
      themeId: theme.id,
      policyCategoryId: policyCategory.id,
    };
  }, [data]);

  const listOfPracticesIDs = selectedListPractices?.map(
    (item: { value: string }) => Number(item?.value)
  );

  const listOfPeopleIDs = selectedListPeople?.map((item: { value: string }) =>
    Number(item?.value)
  );

  /**
   * handleFinish()
   */

  const handleFinish = useCallback(
    async (data: PolicyInputData) => {
      try {
       
        await updatePolicy({
          variables: {
            policyUpdateInput: {
              id: params.policyId,
              policyInput: {
                ...data,
                practices: listOfPracticesIDs,
                people: listOfPeopleIDs,
                ...(imageAttachmentDetails !== null
                  ? {
                      picture: {
                        source:
                          imageAttachmentDetails?.base64URL ||
                          imageAttachmentDetails?.source,
                        originalFilename:
                          imageAttachmentDetails?.name ||
                          imageAttachmentDetails?.originalFilename,
                      },
                    }
                  : {}),
                ...(documentAttachmentDetails !== null
                  ? {
                      document: {
                        source:
                          documentAttachmentDetails?.base64URL ||
                          documentAttachmentDetails?.source,
                        originalFilename:
                          documentAttachmentDetails?.name ||
                          documentAttachmentDetails?.originalFilename,
                      },
                    }
                  : {}),
              },
            },
          },
        });

        navigate(
          isAdmin
            ? `/admin/policies/${params.policyId}`
            : `/policies/${params.policyId}`,
          { replace: true }
        );
      } catch (err) {
        notify.error(err as Error);
      }
    },
    [
      updatePolicy,
      navigate,
      params,
      notify,
      imageAttachmentDetails,
      documentAttachmentDetails,
      listOfPracticesIDs,
      listOfPeopleIDs,
    ]
  );

  /**
   * handleCancel()
   */
  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <Box mb={24}>
      <Header title="Edit policy" />

      <Container sx={{ my: 3 }}>
        <Paper component={Container} sx={{ py: 3 }}>
          <PolicyEditor
            initialValues={initialValues}
            disabled={loading || updating}
            onFinish={handleFinish}
            onCancel={handleCancel}
            isNotPracticeCreateModule={true}
            setSelectedListPractices={setSelectedListPractices}
            selectedListPractices={selectedListPractices}
            setSelectedListPeople={setSelectedListPeople}
            selectedListPeople={selectedListPeople}
            imageAttachmentDetails={imageAttachmentDetails}
            documentAttachmentDetails={documentAttachmentDetails}
            setImageAttachmentDetails={setImageAttachmentDetails}
            setDocumentAttachmentDetails={setDocumentAttachmentDetails}
            policyData={data}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export { PolicyEdit };
